import { useMemo } from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { faBoxArchive, faCalendarXmark } from "@performyard/fontawesome/pro-regular-svg-icons";
import {
	faCalendarDays,
	faCheck,
	faPencil,
	faReply,
	faVideo,
	faVideoSlash,
	faXmark,
} from "@performyard/fontawesome/pro-solid-svg-icons";
import type { MeetingModelJson, VideoConferencingProviderEnum } from "@performyard/service-contracts";

import { useRbacObjectPermissions } from "../../../../api/hooks";

import { hasMeetingStarted } from "../../utils/meetingTiming";

import { config } from "../config";

export type MenuOptionAction = "edit" | "status" | "calendar" | "video-conferencing";

export type MenuOption = {
	id: string;
	label: string;
	action: MenuOptionAction;
	icon: IconDefinition;
};

type UseMeetingOptionsParams = {
	meeting: MeetingModelJson;
	isCalendarIntegrationEnabled: boolean;
	hasCalendarEvent: boolean;
	isCalendarMismatch: boolean;
	isNonOwnerSeriesEvent: boolean;
	isNonOwnerSeriesVideoConference: boolean;
	isVideoConferencingFeatureEnabled: boolean;
	isVideoConferencingEligible: boolean;
	isVideoConferencingAdminEnabled: boolean;
	connectedProviders: { provider: VideoConferencingProviderEnum }[];
};

type UseMeetingOptionsReturn = {
	options: MenuOption[];
	canEdit: boolean;
	canChangeStatus: boolean;
};

/**
 * Hook for generating meeting options based on RBAC permissions and meeting state.
 * @param meeting - The current meeting object
 * @param isCalendarIntegrationEnabled - Whether calendar integration is enabled
 * @param hasCalendarEvent - Whether meeting has a calendar event
 * @param isCalendarMismatch - Whether calendar provider mismatches
 * @param isNonOwnerSeriesEvent - Whether the event is a series occurrence on another user's calendar
 */
export const useMeetingOptions = ({
	meeting,
	isCalendarIntegrationEnabled,
	hasCalendarEvent,
	isCalendarMismatch,
	isNonOwnerSeriesEvent,
	isNonOwnerSeriesVideoConference,
	isVideoConferencingFeatureEnabled,
	isVideoConferencingEligible,
	isVideoConferencingAdminEnabled,
	connectedProviders,
}: UseMeetingOptionsParams): UseMeetingOptionsReturn => {
	const { data: permissions } = useRbacObjectPermissions("meeting", meeting._id);

	const canEditMeeting = permissions?.includes("meetings:editMeeting") ?? false;
	// Backend gates every status change on editMeeting before the per-transition check.
	const canActivate = canEditMeeting && (permissions?.includes("meetings:activateMeeting") ?? false);
	const canComplete = canEditMeeting && (permissions?.includes("meetings:completeMeeting") ?? false);
	const canArchive = canEditMeeting && (permissions?.includes("meetings:archiveMeeting") ?? false);
	const canCancel = canEditMeeting && (permissions?.includes("meetings:cancelMeeting") ?? false);

	const isActiveStatus = meeting.status === "active";
	const canEdit = isActiveStatus && canEditMeeting;
	const canChangeStatus = canActivate || canComplete || canArchive || canCancel;
	const isCompletable = config.statusOptions.includes(meeting.status) && meeting.status !== "completed";
	const hasVideoConferencing = !!meeting.videoConference;

	const options = useMemo((): MenuOption[] => {
		const result: MenuOption[] = [];

		if (canEdit) {
			result.push({ id: "edit", label: "Edit Meeting", action: "edit", icon: faPencil });
		}

		if (isCalendarIntegrationEnabled && canEdit && !isNonOwnerSeriesEvent) {
			if (hasCalendarEvent && !isCalendarMismatch) {
				result.push({
					id: "remove-from-calendar",
					label: "Remove from Calendar",
					action: "calendar",
					icon: faCalendarXmark,
				});
			} else {
				result.push({
					id: "add-to-calendar",
					label: "Add to Calendar",
					action: "calendar",
					icon: faCalendarDays,
				});
			}
		}

		// VC mirrors the calendar pattern. Remove is shown whenever VC is attached even
		// if the user later disconnected, so an orphan link is always clearable — but the
		// whole block is suppressed when the VC feature flag is off or the company has disabled VC.
		if (
			canEdit &&
			isVideoConferencingFeatureEnabled &&
			isVideoConferencingAdminEnabled &&
			(isVideoConferencingEligible || hasVideoConferencing)
		) {
			if (hasVideoConferencing && !isNonOwnerSeriesVideoConference) {
				result.push({
					id: "remove-video-conferencing",
					label: "Remove Video Conferencing",
					action: "video-conferencing",
					icon: faVideoSlash,
				});
			} else if (
				!hasVideoConferencing &&
				connectedProviders.length > 0 &&
				!hasMeetingStarted(meeting.startDateTime)
			) {
				result.push({
					id: `add-video-conferencing`,
					label: "Add Video Conferencing",
					action: "video-conferencing",
					icon: faVideo,
				});
			}
		}

		if (!isActiveStatus && canActivate) {
			result.push({ id: "active", label: "Reopen Meeting", action: "status", icon: faReply });
		}

		if (isCompletable && canComplete) {
			result.push({ id: "completed", label: "Complete Meeting", action: "status", icon: faCheck });
		}

		if (meeting.status !== "archived" && canArchive) {
			result.push({ id: "archived", label: "Archive Meeting", action: "status", icon: faBoxArchive });
		}

		if (isActiveStatus && canCancel) {
			result.push({ id: "canceled", label: "Cancel Meeting", action: "status", icon: faXmark });
		}

		return result;
	}, [
		meeting.status,
		meeting.startDateTime,
		canEdit,
		canActivate,
		canComplete,
		canArchive,
		canCancel,
		isCalendarIntegrationEnabled,
		hasCalendarEvent,
		isCalendarMismatch,
		isNonOwnerSeriesEvent,
		isNonOwnerSeriesVideoConference,
		isActiveStatus,
		isCompletable,
		isVideoConferencingFeatureEnabled,
		isVideoConferencingEligible,
		isVideoConferencingAdminEnabled,
		connectedProviders,
		hasVideoConferencing,
	]);

	return {
		options,
		canEdit,
		canChangeStatus,
	};
};
